// src/components/budgeting/TowerBudgetingCMESummaryModal.jsx
import React, { useState, useEffect } from 'react';
import { X, FileDown, FileSpreadsheet } from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const TR_CODE_GROUPS = {
  MATERIAL: ['TR-0002', 'TR-0003', 'TR-0004', 'TR-0005'],
  JASA: ['TR-0001', 'TR-0008', 'TR-0012', 'TR-0014'],
  OTHER: ['TR-0006', 'TR-0007', 'TR-0009', 'TR-0010', 'TR-0011', 'TR-0013', 'TR-0015', 'TR-0016']
};

const TR_CODE_LABELS = {
  'TR-0001': 'Upah Sipil',
  'TR-0002': 'Material Alam',
  'TR-0003': 'Besi Beton',
  'TR-0004': 'TOWER SST 72 M',
  'TR-0005': 'ME',
  'TR-0006': 'Install PLN',
  'TR-0007': 'Pengiriman Material ke Site',
  'TR-0008': 'Upah ME',
  'TR-0009': 'OPS SPV',
  'TR-0010': 'Bongkar Langsir Besi Beton',
  'TR-0011': 'Bongkar Langsir ME dan Tower',
  'TR-0012': 'Borpile',
  'TR-0013': 'OPS Pengecoran',
  'TR-0014': 'Upah Erection',
  'TR-0015': 'Keamanan',
  'TR-0016': 'Additional'
};

const TowerBudgetingCMESummaryModal = ({ 
  isOpen, 
  onClose, 
  tower, 
  rapData, 
  categories,
  rapType = 'CME'
}) => {
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    if (isOpen && rapData && categories) {
      aggregateData();
    }
  }, [isOpen, rapData, categories]);

  const aggregateData = () => {
    const aggregated = {};
    
    categories.forEach(category => {
      processCategory(category);
    });
    
    function processCategory(cat) {
      const categoryData = rapData[cat.id];
      if (categoryData && categoryData.items) {
        categoryData.items
          .filter(item => !item.isNotUsed && item.trCode)
          .forEach(item => {
            const key = item.trCode;
            
            if (!aggregated[key]) {
              aggregated[key] = {
                trCode: key,
                description: TR_CODE_LABELS[key] || key,
                upahAmount: 0,
                materialAmount: 0,
                totalAmount: 0,
                bosAmount: 0,
                bhoAmount: 0,
                items: []
              };
            }
            
            const amount = parseFloat(item.rapTotalPrice) || 0;
            
            // ✅ FIX: Check item.itemType - SERVICE/JASA → Upah, MATERIAL → Material
            if (item.itemType === 'SERVICE' || item.itemType === 'JASA' || item.itemType === 'LABOR') {
              aggregated[key].upahAmount += amount;
            } else {
              // MATERIAL and everything else → Material column
              aggregated[key].materialAmount += amount;
            }
            
            if (item.postingType === 'BOS') {
              aggregated[key].bosAmount += amount;
            } else if (item.postingType === 'BHO') {
              aggregated[key].bhoAmount += amount;
            }
            
            aggregated[key].totalAmount += amount;
            aggregated[key].items.push(item);
          });
      }
      
      if (cat.children) {
        cat.children.forEach(child => processCategory(child));
      }
    }
    
    const grouped = {
      material: [],
      jasa: [],
      other: []
    };
    
    Object.values(aggregated).forEach(item => {
      if (TR_CODE_GROUPS.MATERIAL.includes(item.trCode)) {
        grouped.material.push(item);
      } else if (TR_CODE_GROUPS.JASA.includes(item.trCode)) {
        grouped.jasa.push(item);
      } else {
        grouped.other.push(item);
      }
    });
    
    const subtotals = {
      material: grouped.material.reduce((sum, item) => sum + item.totalAmount, 0),
      jasa: grouped.jasa.reduce((sum, item) => sum + item.totalAmount, 0),
      other: grouped.other.reduce((sum, item) => sum + item.totalAmount, 0)
    };
    
    const totalBOS = Object.values(aggregated).reduce((sum, item) => sum + item.bosAmount, 0);
    const totalBHO = Object.values(aggregated).reduce((sum, item) => sum + item.bhoAmount, 0);
    
    setSummaryData({
      grouped,
      subtotals,
      totalBOS,
      totalBHO,
      grandTotal: totalBOS + totalBHO
    });
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getMostCommonPosting = (items) => {
    const counts = {};
    items.forEach(item => {
      if (item.postingType) {
        counts[item.postingType] = (counts[item.postingType] || 0) + 1;
      }
    });
    
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted.length > 0 ? sorted[0][0] : '-';
  };

  const exportToPDF = () => {
    if (!summaryData) return;
    
    const doc = new jsPDF('landscape');
    
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Summary Alokasi Dana Project untuk Head office dan On site', 14, 15);
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`${tower.siteName} (${tower.siteId})`, 14, 22);
    doc.setFontSize(10);
    doc.text(`Province: ${tower.provinsi} | ${rapType} RAP`, 14, 28);
    
    const tableData = [];
    let rowNum = 1;
    
    tableData.push([{ content: 'I. Material', colSpan: 8, styles: { fontStyle: 'bold', fillColor: [200, 200, 200] } }]);
    summaryData.grouped.material.forEach(item => {
      tableData.push([
        rowNum++,
        item.description,
        item.trCode,
        'ls', '1',
        formatCurrency(item.upahAmount),
        formatCurrency(item.materialAmount),
        getMostCommonPosting(item.items)
      ]);
    });
    tableData.push([
      { content: 'Sub Total Material', colSpan: 6, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: formatCurrency(summaryData.subtotals.material), styles: { fontStyle: 'bold' } }, ''
    ]);
    
    tableData.push([{ content: 'II. Jasa', colSpan: 8, styles: { fontStyle: 'bold', fillColor: [200, 200, 200] } }]);
    summaryData.grouped.jasa.forEach(item => {
      tableData.push([
        rowNum++,
        item.description,
        item.trCode,
        'ls', '1',
        formatCurrency(item.upahAmount),
        formatCurrency(item.materialAmount),
        getMostCommonPosting(item.items)
      ]);
    });
    tableData.push([
      { content: 'Sub Total Jasa', colSpan: 6, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: formatCurrency(summaryData.subtotals.jasa), styles: { fontStyle: 'bold' } }, ''
    ]);
    
    if (summaryData.grouped.other.length > 0) {
      tableData.push([{ content: 'III. Other', colSpan: 8, styles: { fontStyle: 'bold', fillColor: [200, 200, 200] } }]);
      summaryData.grouped.other.forEach(item => {
        tableData.push([
          rowNum++,
          item.description,
          item.trCode,
          'ls', '1',
          formatCurrency(item.upahAmount),
          formatCurrency(item.materialAmount),
          getMostCommonPosting(item.items)
        ]);
      });
      tableData.push([
        { content: 'Sub Total Other', colSpan: 6, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: formatCurrency(summaryData.subtotals.other), styles: { fontStyle: 'bold' } }, ''
      ]);
    }
    
    tableData.push([
      { content: 'Total Posting On site (BOS)', colSpan: 6, styles: { fontStyle: 'bold', fillColor: [255, 255, 200], halign: 'right' } },
      { content: formatCurrency(summaryData.totalBOS), styles: { fontStyle: 'bold', fillColor: [255, 255, 200] } }, ''
    ]);
    tableData.push([
      { content: 'Total Posting HO (BHO)', colSpan: 6, styles: { fontStyle: 'bold', fillColor: [255, 255, 200], halign: 'right' } },
      { content: formatCurrency(summaryData.totalBHO), styles: { fontStyle: 'bold', fillColor: [255, 255, 200] } }, ''
    ]);
    tableData.push([
      { content: 'TOTAL', colSpan: 6, styles: { fontStyle: 'bold', fillColor: [255, 255, 100], halign: 'right', fontSize: 12 } },
      { content: formatCurrency(summaryData.grandTotal), styles: { fontStyle: 'bold', fillColor: [255, 255, 100], fontSize: 12 } }, ''
    ]);
    
    doc.autoTable({
      head: [['NO', 'DESCRIPTION', 'kode', 'UNIT', 'VOL', 'Upah', 'Material', 'REMARKS']],
      body: tableData,
      startY: 35,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185], fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 70 },
        2: { cellWidth: 25, halign: 'center' },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 15, halign: 'center' },
        5: { cellWidth: 35, halign: 'right' },
        6: { cellWidth: 35, halign: 'right' },
        7: { cellWidth: 25, halign: 'center' }
      }
    });
    
    doc.save(`RAP_Summary_${tower.siteId}_${rapType}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = () => {
    if (!summaryData) return;
    
    const excelData = [];
    let rowNum = 1;
    
    excelData.push(['Summary Alokasi Dana Project untuk Head office dan On site']);
    excelData.push([`${tower.siteName} (${tower.siteId})`]);
    excelData.push([`Province: ${tower.provinsi} | ${rapType} RAP`]);
    excelData.push([]);
    excelData.push(['NO', 'DESCRIPTION', 'kode', 'UNIT', 'VOL', 'Upah', 'Material', 'REMARKS']);
    
    excelData.push(['I. Material']);
    summaryData.grouped.material.forEach(item => {
      excelData.push([rowNum++, item.description, item.trCode, 'ls', 1, item.upahAmount, item.materialAmount, getMostCommonPosting(item.items)]);
    });
    excelData.push(['', '', '', '', 'Sub Total Material', '', summaryData.subtotals.material, '']);
    excelData.push([]);
    
    excelData.push(['II. Jasa']);
    summaryData.grouped.jasa.forEach(item => {
      excelData.push([rowNum++, item.description, item.trCode, 'ls', 1, item.upahAmount, item.materialAmount, getMostCommonPosting(item.items)]);
    });
    excelData.push(['', '', '', '', 'Sub Total Jasa', '', summaryData.subtotals.jasa, '']);
    excelData.push([]);
    
    if (summaryData.grouped.other.length > 0) {
      excelData.push(['III. Other']);
      summaryData.grouped.other.forEach(item => {
        excelData.push([rowNum++, item.description, item.trCode, 'ls', 1, item.upahAmount, item.materialAmount, getMostCommonPosting(item.items)]);
      });
      excelData.push(['', '', '', '', 'Sub Total Other', '', summaryData.subtotals.other, '']);
      excelData.push([]);
    }
    
    excelData.push(['', '', '', '', 'Total Posting On site (BOS)', '', summaryData.totalBOS, '']);
    excelData.push(['', '', '', '', 'Total Posting HO (BHO)', '', summaryData.totalBHO, '']);
    excelData.push(['', '', '', '', 'TOTAL', '', summaryData.grandTotal, '']);
    
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    ws['!cols'] = [
      { wch: 5 }, { wch: 35 }, { wch: 12 }, { wch: 8 }, { wch: 8 }, { wch: 18 }, { wch: 18 }, { wch: 12 }
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RAP Summary');
    XLSX.writeFile(wb, `RAP_Summary_${tower.siteId}_${rapType}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const renderGroupRows = (groupItems, startNum) => {
    return groupItems.map((item, index) => (
      <tr key={item.trCode} className="border-b border-gray-300 hover:bg-gray-50">
        <td className="px-4 py-2 text-center text-sm border-r border-gray-300">{startNum + index}</td>
        <td className="px-4 py-2 text-sm border-r border-gray-300">{item.description}</td>
        <td className="px-4 py-2 text-center text-sm font-mono border-r border-gray-300">{item.trCode}</td>
        <td className="px-4 py-2 text-center text-sm border-r border-gray-300">ls</td>
        <td className="px-4 py-2 text-center text-sm border-r border-gray-300">1</td>
        <td className="px-4 py-2 text-right text-sm font-medium border-r border-gray-300">
          {item.upahAmount > 0 ? formatCurrency(item.upahAmount) : '-'}
        </td>
        <td className="px-4 py-2 text-right text-sm font-medium border-r border-gray-300">
          {item.materialAmount > 0 ? formatCurrency(item.materialAmount) : '-'}
        </td>
        <td className="px-4 py-2 text-center text-sm">{getMostCommonPosting(item.items)}</td>
      </tr>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-[80%] max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between rounded-t-lg flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">
              Summary Alokasi Dana Project untuk Head office dan On site
            </h2>
            <p className="text-sm text-white opacity-90 mt-1">
              {tower.siteName} ({tower.siteId}) - {rapType} RAP
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportToPDF} disabled={!summaryData} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
              <FileDown className="w-4 h-4" />
              PDF
            </button>
            <button onClick={exportToExcel} disabled={!summaryData} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
              <FileSpreadsheet className="w-4 h-4" />
              Excel
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {!summaryData ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">Loading summary data...</p>
            </div>
          ) : (
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="px-4 py-3 text-center text-sm font-bold border-r border-white">NO</th>
                  <th className="px-4 py-3 text-left text-sm font-bold border-r border-white">DESCRIPTION</th>
                  <th className="px-4 py-3 text-center text-sm font-bold border-r border-white">kode</th>
                  <th className="px-4 py-3 text-center text-sm font-bold border-r border-white">UNIT</th>
                  <th className="px-4 py-3 text-center text-sm font-bold border-r border-white">VOL</th>
                  <th className="px-4 py-3 text-center text-sm font-bold border-r border-white">Upah</th>
                  <th className="px-4 py-3 text-center text-sm font-bold border-r border-white">Material</th>
                  <th className="px-4 py-3 text-center text-sm font-bold">REMARKS</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-gray-200">
                  <td colSpan="8" className="px-4 py-2 font-bold text-sm border-b border-gray-300">I. Material</td>
                </tr>
                {renderGroupRows(summaryData.grouped.material, 1)}
                <tr className="bg-gray-100 font-bold">
                  <td colSpan="6" className="px-4 py-3 text-right border-r border-gray-300">Sub Total Material</td>
                  <td className="px-4 py-3 text-right border-r border-gray-300">{formatCurrency(summaryData.subtotals.material)}</td>
                  <td></td>
                </tr>

                <tr className="bg-gray-200">
                  <td colSpan="8" className="px-4 py-2 font-bold text-sm border-b border-gray-300">II. Jasa</td>
                </tr>
                {renderGroupRows(summaryData.grouped.jasa, summaryData.grouped.material.length + 1)}
                <tr className="bg-gray-100 font-bold">
                  <td colSpan="6" className="px-4 py-3 text-right border-r border-gray-300">Sub Total Jasa</td>
                  <td className="px-4 py-3 text-right border-r border-gray-300">{formatCurrency(summaryData.subtotals.jasa)}</td>
                  <td></td>
                </tr>

                {summaryData.grouped.other.length > 0 && (
                  <>
                    <tr className="bg-gray-200">
                      <td colSpan="8" className="px-4 py-2 font-bold text-sm border-b border-gray-300">III. Other</td>
                    </tr>
                    {renderGroupRows(summaryData.grouped.other, summaryData.grouped.material.length + summaryData.grouped.jasa.length + 1)}
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan="6" className="px-4 py-3 text-right border-r border-gray-300">Sub Total Other</td>
                      <td className="px-4 py-3 text-right border-r border-gray-300">{formatCurrency(summaryData.subtotals.other)}</td>
                      <td></td>
                    </tr>
                  </>
                )}

                <tr className="bg-yellow-100 font-bold text-base">
                  <td colSpan="6" className="px-4 py-3 text-right border-r border-gray-300">Total Posting On site (BOS)</td>
                  <td className="px-4 py-3 text-right border-r border-gray-300">{formatCurrency(summaryData.totalBOS)}</td>
                  <td></td>
                </tr>
                <tr className="bg-yellow-100 font-bold text-base">
                  <td colSpan="6" className="px-4 py-3 text-right border-r border-gray-300">Total Posting HO (BHO)</td>
                  <td className="px-4 py-3 text-right border-r border-gray-300">{formatCurrency(summaryData.totalBHO)}</td>
                  <td></td>
                </tr>
                <tr className="bg-yellow-300 font-bold text-lg">
                  <td colSpan="6" className="px-4 py-4 text-right border-r border-gray-300">TOTAL</td>
                  <td className="px-4 py-4 text-right border-r border-gray-300">{formatCurrency(summaryData.grandTotal)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TowerBudgetingCMESummaryModal;